// OpenAI API (api.openai.com /v1/chat/completions) のRESTアダプタ。
// reasoningモデル（gpt-5系等）の思考は応答に含まれないため、履歴の再現は
// テキストとツール呼び出しのみで完結する。

import {
  postJsonWithRetry,
  retryAfterMs,
  type ChatContent,
  type ChatPart,
  type GenerateRequest,
  type GenerateResponse,
  type ThinkingConfig,
} from './llm.js'

const API_URL = 'https://api.openai.com/v1/chat/completions'

const REASONING_EFFORTS = new Set(['high', 'medium', 'low', 'minimal', 'none'])

/** 思考指定を reasoning_effort に変換する（xhigh/max は high に丸める。数値指定は無視） */
function reasoningEffort(config?: ThinkingConfig): string | undefined {
  const level = config?.thinkingLevel
  if (!level) return undefined
  if (REASONING_EFFORTS.has(level)) return level
  if (level === 'xhigh' || level === 'max') return 'high'
  return undefined
}

interface OpenAIToolCall {
  id: string
  type: 'function'
  function: { name: string; arguments: string }
}

type OpenAIMessage = Record<string, unknown>

/**
 * 中立フォーマットの履歴を Chat Completions の messages 形式に変換する。
 * 思考ブロックは送らない。Gemini産の履歴には functionCall の id が無いため、
 * 呼び出しと応答の順序対応を保ったまま合成IDで補う。
 */
function toOpenAIMessages(systemInstruction: string, contents: ChatContent[]): OpenAIMessage[] {
  const messages: OpenAIMessage[] = [{ role: 'system', content: systemInstruction }]
  let syntheticIdCounter = 0
  let pendingSyntheticIds: string[] = []

  for (const content of contents) {
    if (content.role === 'model') {
      pendingSyntheticIds = []
      const texts: string[] = []
      const toolCalls: OpenAIToolCall[] = []
      for (const part of content.parts) {
        if (part.thought || part.redactedThinking !== undefined) continue
        if (part.functionCall) {
          let id = part.functionCall.id
          if (!id) {
            id = `legacy_call_${syntheticIdCounter++}`
            pendingSyntheticIds.push(id)
          }
          toolCalls.push({
            id,
            type: 'function',
            function: {
              name: part.functionCall.name,
              arguments: JSON.stringify(part.functionCall.args),
            },
          })
          continue
        }
        if (part.text) texts.push(part.text)
      }
      if (texts.length === 0 && toolCalls.length === 0) continue
      messages.push({
        role: 'assistant',
        content: texts.length > 0 ? texts.join('\n\n') : null,
        ...(toolCalls.length > 0 ? { tool_calls: toolCalls } : {}),
      })
      continue
    }

    for (const part of content.parts) {
      if (part.functionResponse) {
        messages.push({
          role: 'tool',
          tool_call_id: part.functionResponse.id ?? pendingSyntheticIds.shift() ?? '',
          content: JSON.stringify(part.functionResponse.response),
        })
      } else if (part.text) {
        messages.push({ role: 'user', content: part.text })
      }
    }
  }

  return messages
}

interface OpenAIResponse {
  choices?: Array<{
    message?: {
      content?: string | null
      refusal?: string | null
      tool_calls?: Array<{
        id?: string
        function?: { name?: string; arguments?: string }
      }>
    }
    finish_reason?: string
  }>
}

function toGenerateResponse(json: OpenAIResponse): GenerateResponse {
  const choice = json.choices?.[0]
  if (!choice?.message) {
    return { candidates: [] }
  }

  if (choice.message.refusal) {
    return { candidates: [], promptFeedback: { blockReason: '安全システムによる拒否' } }
  }
  if (choice.finish_reason === 'content_filter') {
    return { candidates: [], promptFeedback: { blockReason: 'コンテンツフィルター' } }
  }

  const parts: ChatPart[] = []
  if (choice.message.content) {
    parts.push({ text: choice.message.content })
  }
  for (const call of choice.message.tool_calls ?? []) {
    let args: Record<string, unknown> = {}
    try {
      const parsed: unknown = JSON.parse(call.function?.arguments ?? '{}')
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        args = parsed as Record<string, unknown>
      }
    } catch {
      // 不正なJSON引数は空オブジェクトとして扱う（ツール側のバリデーションに任せる）
    }
    parts.push({
      functionCall: {
        name: call.function?.name ?? '',
        args,
        ...(call.id ? { id: call.id } : {}),
      },
    })
  }

  if (parts.length === 0) {
    return { candidates: [] }
  }

  return {
    candidates: [
      {
        content: { role: 'model', parts },
        ...(choice.finish_reason === 'length' ? { finishReason: 'MAX_TOKENS' } : {}),
      },
    ],
  }
}

function extractErrorMessage(body: unknown): string | undefined {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error?: { message?: string } }).error
    return error?.message
  }
  return undefined
}

export async function generateOpenAI(request: GenerateRequest): Promise<GenerateResponse> {
  const { apiKey, model, contents, systemInstruction, functionDeclarations } = request

  const effort = reasoningEffort(model.thinkingConfig)

  const json = await postJsonWithRetry({
    provider: 'openai',
    url: API_URL,
    headers: { Authorization: `Bearer ${apiKey}` },
    body: {
      model: model.modelId,
      messages: toOpenAIMessages(systemInstruction, contents),
      tools: functionDeclarations.map((decl) => ({
        type: 'function',
        function: {
          name: decl.name,
          description: decl.description,
          parameters: decl.parameters,
        },
      })),
      ...(effort ? { reasoning_effort: effort } : {}),
    },
    retryableStatuses: new Set([429, 500, 503]),
    extractErrorMessage,
    extractRetryDelayMs: (_json, response) => retryAfterMs(response),
    connectErrorMessage: 'OpenAI APIに接続できませんでした。ネットワークを確認してください。',
  })

  return toGenerateResponse(json as OpenAIResponse)
}
